import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  FormControlLabel,
  Switch,
  Alert,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { NewsArticle } from '../../types/News';
import { LocalizedText, emptyLocalizedText } from '../../types/LocalizedText';
import { LocalizedFieldsTabs } from '../common/LocalizedTextFields';
import { useLanguages } from '../../hooks/useLanguages';
import {
  getMissingRequiredLanguageLabels,
  hasLocalizedText,
  normalizeLocalizedText,
  serializeLocalizedText,
} from '../../utils/localizedText';
import NewsImageUpload from './NewsImageUpload';

interface NewsFormData {
  title: LocalizedText;
  excerpt: LocalizedText;
  content: LocalizedText;
  isPublished: boolean;
  publishedAt: string;
  image?: File | string;
}

const NewsForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditMode = !!id;
  const { activeLanguages, languageCodes } = useLanguages();
  const [formData, setFormData] = useState<NewsFormData>({
    title: emptyLocalizedText(),
    excerpt: emptyLocalizedText(),
    content: emptyLocalizedText(),
    isPublished: false,
    publishedAt: '',
    image: undefined,
  });
  const [formError, setFormError] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  const newsFields = useMemo(
    () => [
      {
        key: 'title',
        label: t('news.articleTitle'),
        required: true,
      },
      {
        key: 'excerpt',
        label: t('news.excerpt'),
        multiline: true,
        rows: 3,
        htmlAllowed: true,
      },
      {
        key: 'content',
        label: t('news.content'),
        multiline: true,
        rows: 12,
        required: true,
        htmlAllowed: true,
      },
    ],
    [t]
  );

  const { data: article, isLoading } = useQuery({
    queryKey: ['news', id],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/news/${id}`, {
        params: { includeUnpublished: true },
      });
      return response.data as NewsArticle;
    },
    enabled: isEditMode,
  });

  useEffect(() => {
    if (!isEditMode) {
      setIsInitialized(false);
      setFormData({
        title: emptyLocalizedText(languageCodes),
        excerpt: emptyLocalizedText(languageCodes),
        content: emptyLocalizedText(languageCodes),
        isPublished: false,
        publishedAt: '',
        image: undefined,
      });
      setFormError('');
    }
  }, [isEditMode, languageCodes]);

  useEffect(() => {
    if (article && !isInitialized && article.id) {
      const imageValue =
        article.hasImage === true
          ? `${process.env.REACT_APP_API_URL}/news/${article.id}/image?includeUnpublished=true`
          : undefined;

      setFormData({
        title: normalizeLocalizedText(article.title, languageCodes),
        excerpt: normalizeLocalizedText(article.excerpt, languageCodes),
        content: normalizeLocalizedText(article.content, languageCodes),
        isPublished: article.isPublished ?? false,
        publishedAt: article.publishedAt ?? '',
        image: article.hasImage === false ? undefined : imageValue,
      });
      setIsInitialized(true);
    }
  }, [article, isInitialized, languageCodes]);

  const mutation = useMutation({
    mutationFn: async (data: NewsFormData) => {
      const payload = new FormData();
      payload.append('title', JSON.stringify(serializeLocalizedText(data.title, activeLanguages)));
      payload.append('content', JSON.stringify(serializeLocalizedText(data.content, activeLanguages)));

      if (hasLocalizedText(data.excerpt)) {
        payload.append(
          'excerpt',
          JSON.stringify(
            serializeLocalizedText(data.excerpt, activeLanguages, { omitEmptyOptional: true })
          )
        );
      }

      payload.append('isPublished', data.isPublished ? 'true' : 'false');

      if (data.isPublished) {
        payload.append('publishedAt', data.publishedAt || new Date().toISOString());
      } else {
        payload.append('publishedAt', '');
      }

      if (data.image instanceof File) {
        payload.append('image', data.image);
      }

      if (isEditMode) {
        return axios.put(`${process.env.REACT_APP_API_URL}/news/${id}`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      return axios.post(`${process.env.REACT_APP_API_URL}/news`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      if (isEditMode && id) {
        queryClient.invalidateQueries({ queryKey: ['news', id] });
      }
      navigate('/news');
    },
    onError: (err: unknown) => {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setFormError(axiosError.response?.data?.error || t('news.saveFailed'));
    },
  });

  const handleSubmit = () => {
    const missingTitle = getMissingRequiredLanguageLabels(formData.title, activeLanguages);
    if (missingTitle.length > 0) {
      setFormError(
        t('localizedText.missingRequiredLanguages', { languages: missingTitle.join(', ') })
      );
      return;
    }

    const missingContent = getMissingRequiredLanguageLabels(formData.content, activeLanguages);
    if (missingContent.length > 0) {
      setFormError(
        t('localizedText.missingRequiredLanguages', { languages: missingContent.join(', ') })
      );
      return;
    }

    setFormError('');
    mutation.mutate(formData);
  };

  if (isEditMode && isLoading) {
    return <Typography>{t('common.loading')}</Typography>;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        {isEditMode ? t('news.editArticle') : t('news.addArticle')}
      </Typography>

      {formError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formError}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <LocalizedFieldsTabs
            sectionTitle={t('news.localizedContent')}
            fields={newsFields}
            values={{
              title: formData.title,
              excerpt: formData.excerpt,
              content: formData.content,
            }}
            onChange={(updatedValues) =>
              setFormData((prev) => ({
                ...prev,
                title: updatedValues.title ?? prev.title,
                excerpt: updatedValues.excerpt ?? prev.excerpt,
                content: updatedValues.content ?? prev.content,
              }))
            }
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isPublished}
                  onChange={(e) => {
                    const isPublished = e.target.checked;
                    setFormData((prev) => ({
                      ...prev,
                      isPublished,
                      publishedAt: isPublished ? new Date().toISOString() : '',
                    }));
                  }}
                />
              }
              label={t('news.published')}
            />

            {formData.isPublished && formData.publishedAt && (
              <Typography variant="body2" color="text.secondary">
                {t('news.publishedAt')}: {new Date(formData.publishedAt).toLocaleString()}
              </Typography>
            )}

            <NewsImageUpload
              value={formData.image}
              onChange={(file) =>
                setFormData((prev) => ({ ...prev, image: file || undefined }))
              }
              disabled={mutation.isPending}
              articleId={id}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => navigate('/news')}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? t('news.saving') : t('common.save')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default NewsForm;
